function normalizeIdentifier(value) {
	if (value === undefined || value === null) {
		return "";
	}
	return String(value).trim();
}

export function getStatusSelectionId(item = {}) {
	const source = item && typeof item === "object" ? item : {};
	return normalizeIdentifier(source.instanceId || source.objectID);
}

/**
 * Selection state deliberately contains identity/locator data only. Search-hit
 * content belongs to the operation that consumes the selection and is hydrated
 * there with an explicit projection.
 */
export function toStatusSelectionRef(item = {}) {
	const instanceId = getStatusSelectionId(item);
	if (!instanceId) {
		return null;
	}

	const objectID = normalizeIdentifier(item.objectID);
	const url = typeof item.url === "string" ? item.url.trim() : "";
	return {
		instanceId,
		...(objectID ? { objectID } : {}),
		...(url ? { url } : {})
	};
}

export function normalizeStatusSelectionRefs(items = []) {
	const refs = [];
	const indexesById = new Map();
	(Array.isArray(items) ? items : []).forEach((item) => {
		const ref = toStatusSelectionRef(item);
		if (!ref) {
			return;
		}

		const existingIndex = indexesById.get(ref.instanceId);
		if (existingIndex !== undefined) {
			refs[existingIndex] = { ...refs[existingIndex], ...ref };
			return;
		}

		indexesById.set(ref.instanceId, refs.length);
		refs.push(ref);
	});
	return refs;
}

export function buildExactStatusSelectionRefs(items = [], expectedCount = null) {
	const sourceItems = Array.isArray(items) ? items : [];
	const invalidItems = sourceItems.filter((item) => {
		const ref = toStatusSelectionRef(item);
		return !ref || !ref.url;
	});
	if (invalidItems.length > 0) {
		throw new Error(`${invalidItems.length} matching ${invalidItems.length === 1 ? "record is" : "records are"} missing an instance ID or storage URL. Nothing was selected; refresh the Status page and try again.`);
	}

	const refs = normalizeStatusSelectionRefs(sourceItems);
	if (refs.length !== sourceItems.length) {
		const duplicateCount = sourceItems.length - refs.length;
		throw new Error(`The filtered result contained ${duplicateCount} duplicate ${duplicateCount === 1 ? "instance" : "instances"}. Nothing was selected; refresh the Status page and try again.`);
	}
	if (Number.isFinite(expectedCount) && sourceItems.length !== expectedCount) {
		throw new Error(`The visible filter showed ${expectedCount} ${expectedCount === 1 ? "record" : "records"}, but Select All resolved ${sourceItems.length}. Nothing was selected because the filter or index changed; refresh and try again.`);
	}
	return refs;
}

export function mergeStatusSelectionData(refs = [], hydratedItems = []) {
	const normalizedRefs = normalizeStatusSelectionRefs(refs);
	const hydratedById = new Map();
	(Array.isArray(hydratedItems) ? hydratedItems : []).forEach((item) => {
		const id = getStatusSelectionId(item);
		if (id) {
			hydratedById.set(id, item);
		}
	});

	return normalizedRefs.map((ref) => ({
		...(hydratedById.get(ref.instanceId) || {}),
		...ref
	}));
}

export function removeDeletedStatusSelectionRefs(items = [], deletedItems = []) {
	const deletedById = new Map(normalizeStatusSelectionRefs(deletedItems).map(ref => [ref.instanceId, ref]));
	const deletedSessionIds = new Set((Array.isArray(deletedItems) ? deletedItems : [])
		.filter(item => item && item.deletionScope === "session")
		.map(getStatusSelectionId)
		.filter(Boolean));
	return normalizeStatusSelectionRefs(items).filter(ref => {
		if (deletedSessionIds.has(ref.instanceId)) {
			return false;
		}
		const deletedRef = deletedById.get(ref.instanceId);
		return !deletedRef
			|| (ref.url && ref.url !== deletedRef.url)
			|| (ref.objectID && deletedRef.objectID && ref.objectID !== deletedRef.objectID);
	});
}

export function isDeletedStatusItem(item, deletedItems = []) {
	const ref = toStatusSelectionRef(item);
	if (!ref) {
		return false;
	}
	return (Array.isArray(deletedItems) ? deletedItems : []).some(deletedItem => {
		const deletedRef = toStatusSelectionRef(deletedItem);
		return deletedRef && deletedRef.instanceId === ref.instanceId
			&& (deletedItem.deletionScope === "session" || (ref.url && deletedRef.url === ref.url));
	});
}
